import { useState, useCallback, useRef, useMemo, type RefObject } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useJobCacheInvalidation } from "../../hooks/useJobCacheInvalidation";
import { useJobFilePolling } from "../../hooks/useJobFilePolling";
import { useOpsJobActions } from "../../hooks/useOpsJobActions";
import { useJobProcessingResults } from "../../hooks/useJobProcessingResults";
import { Drawer, Box, Alert, useTheme, useMediaQuery } from "@mui/material";
import {
  useGetJobAsUser,
  generateProcessingResultUploadUrl,
  type JobResponse,
} from "@taxassist/api-client";

import { useCurrentFirmId } from "../../contexts/FirmContext";
import { JobDetailsProvider } from "../../contexts/JobDetailsContext";
import { JobAssignmentDialog } from "./JobAssignmentDialog";
import { JobDetailsHeader } from "./JobDetailsHeader";
import { OpsJobDetailsView } from "./OpsJobDetailsView";
import { ClientJobDetailsView } from "./ClientJobDetailsView";
import { type ProcessingArtifactsRef } from "./ProcessingArtifacts";
import { FileUploadDialog } from "./FileUploadDialog";
import { FileViewerDialog } from "./FileViewerDialog";
import {
  type FileItem,
  convertProcessingArtifactToGeneric,
  convertUploadedFileToGeneric,
  getUploadedFileTypeLabel,
  type ProcessingArtifactType,
  type UploadedFileType,
} from "./fileList";
import { LoadingSpinner } from "../common/LoadingSpinner";
import type { FileUploadItem } from "../../hooks/useFileUpload";
import { getCachedFilePreviewUrl } from "../../utils/filePreviewUtils";
import { useFileActions } from "../../hooks/useFileActions";
import { useNotification } from "../../hooks/useNotification";

// Define the artifact type to match the API client expectations
type ArtifactType = "binder" | "k1_worksheets" | "supporting_doc" | "other";

interface JobDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  firmId?: string; // Required for API call, can be extracted from job if not provided
  anchor?: "left" | "right";
  variant?: "temporary" | "drawer" | "panel"; // Controls rendering mode
  context?: "client" | "ops"; // New prop to control ops-specific features
}

export function JobDetailsDrawer({
  open,
  onClose,
  jobId,
  firmId,
  anchor = "right",
  variant = "drawer",
  context = "client",
}: JobDetailsDrawerProps) {
  const theme = useTheme();
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  const { invalidateJobRelatedQueries, invalidateOpsQueries } =
    useJobCacheInvalidation();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);

  // Ref for ProcessingArtifacts component
  const processingArtifactsRef = useRef<ProcessingArtifactsRef>(null);

  // Notification system
  const notification = useNotification();

  // Get current firm ID as fallback if not provided
  const currentFirmId = useCurrentFirmId();
  const effectiveFirmId = firmId || currentFirmId;

  // Fetch job data
  const {
    data: job,
    isLoading,
    error,
    refetch,
  } = useGetJobAsUser(effectiveFirmId as string, jobId, {
    query: {
      enabled: open && !!jobId && !!effectiveFirmId,
      refetchOnWindowFocus: false,
      queryKey: ["job", effectiveFirmId, jobId],
      staleTime: 0, // Always refetch when requested
    },
  });

  // File polling hook for upload processing
  const { startPolling } = useJobFilePolling({
    refetch,
    isOpen: open,
    onSuccess: async () => {
      // When polling detects files are processed, invalidate job lists
      await invalidateJobRelatedQueries({
        firmId: effectiveFirmId || undefined,
        jobId,
      });
    },
  });

  const opsActions = useOpsJobActions({
    job: job || null,
    onJobUpdate: async () => {
      await refetch();
      await invalidateJobRelatedQueries({
        firmId: effectiveFirmId || undefined,
        jobId,
      });
    },
  });

  // Convert uploaded files to FileItem format for consistency
  const uploadedFileItems: FileItem[] = useMemo(() => {
    const lastSubmittedDate = job?.lastSubmittedAt
      ? new Date(job.lastSubmittedAt)
      : null;

    return (job?.uploadedFiles || []).map((f) => {
      const baseFile = convertUploadedFileToGeneric(f);
      const documentType = (f as any).documentType as
        | UploadedFileType
        | undefined;

      const isNew =
        lastSubmittedDate && job?.status !== "DRAFT"
          ? new Date(f.uploadedAt) > lastSubmittedDate
          : false;

      return {
        ...baseFile,
        type: documentType,
        typeLabel: documentType
          ? getUploadedFileTypeLabel(documentType)
          : undefined,
        metadata: { isProcessingResult: false },
        isNew,
      };
    });
  }, [job?.uploadedFiles, job?.lastSubmittedAt, job?.status]);

  // Fetch processing results using unified hook
  const {
    data: processingResultsData,
    isLoading: isLoadingProcessingResults,
    error: processingResultsError,
    refetch: refetchProcessingResults,
  } = useJobProcessingResults({
    jobId,
    context,
    firmId: effectiveFirmId!,
    enabled: open && !!jobId,
  });

  const onArtifactUpdate = useCallback(async () => {
    refetchProcessingResults();
    refetch(); // Refetch job data to update status badge
    // Invalidate job list queries to update status in job lists
    await invalidateJobRelatedQueries({
      firmId: effectiveFirmId || undefined,
    });
  }, [
    refetchProcessingResults,
    refetch,
    invalidateJobRelatedQueries,
    effectiveFirmId,
  ]);

  // Convert processing results to FileItem format using the helper function
  const processingArtifactItems: FileItem[] = useMemo(
    () =>
      processingResultsData?.processingResults?.map((apiArtifact) => ({
        ...convertProcessingArtifactToGeneric({
          ...apiArtifact,
          // Convert null to undefined and ensure artifactType matches expected enum
          description: apiArtifact.description ?? undefined,
          artifactType: apiArtifact.artifactType as ProcessingArtifactType,
        }),
        metadata: { isProcessingResult: true },
      })) || [],
    [processingResultsData?.processingResults],
  );

  // Use the new useFileActions hook to centralize file handling logic
  const fileActions = useFileActions({
    job: job || null,
    onSuccess: notification.showSuccess,
    onError: notification.showError,
    onPreviewOpen: (_, __, index) => {
      setSelectedFileIndex(index);
      setFileViewerOpen(true);
    },
    onFileUpdate: refetch, // Refresh job data after file updates
  });

  // File action handlers for uploaded files
  const handleFileDownload = useCallback(
    async (file: FileItem) => {
      await fileActions.handleDownload(file);
    },
    [fileActions],
  );

  // Wrapper for deleting uploaded files - passes the correct fileType parameter
  const handleUploadedFileDelete = useCallback(
    async (file: FileItem) => {
      await fileActions.handleDelete(file, "uploaded");
      // fileActions.handleDelete calls onFileUpdate which triggers refetch
      // Also invalidate job cache to ensure fresh data across the app
      await invalidateJobRelatedQueries({
        firmId: effectiveFirmId || undefined,
        jobId,
      });
      // In ops context, force refetch of ops job list to update file counts
      if (context === "ops") {
        await queryClient.refetchQueries({
          queryKey: ["ops-jobs"],
          type: "active",
        });
      }
    },
    [
      fileActions,
      invalidateJobRelatedQueries,
      queryClient,
      context,
      effectiveFirmId,
      jobId,
    ],
  );

  // Wrapper for deleting processing artifacts/deliverables - passes the correct fileType parameter
  const handleProcessingArtifactDelete = useCallback(
    async (file: FileItem) => {
      await fileActions.handleDelete(file, "processing");
      // Refetch processing results after deletion
      await refetchProcessingResults();
      // Also invalidate job cache to keep UI in sync
      await invalidateJobRelatedQueries({
        firmId: effectiveFirmId || undefined,
        jobId,
      });
      // In ops context, force refetch of ops job list to update file counts
      if (context === "ops") {
        await queryClient.refetchQueries({
          queryKey: ["ops-jobs"],
          type: "active",
        });
      }
    },
    [
      fileActions,
      refetchProcessingResults,
      invalidateJobRelatedQueries,
      queryClient,
      context,
      effectiveFirmId,
      jobId,
    ],
  );

  // Function to get presigned URLs for processing result files
  const getProcessingResultFileUrl = useCallback(
    async (file: FileItem): Promise<string> => {
      // Mark file as processing result and delegate to hook
      const fileWithMetadata = {
        ...file,
        metadata: { ...file.metadata, isProcessingResult: true },
      };
      return await fileActions.getFileUrl(fileWithMetadata, "processing");
    },
    [fileActions],
  );

  const handleProcessingResultUpload = useCallback(
    async (file: File, artifactType: ArtifactType, description?: string) => {
      try {
        // Get current count before upload
        const currentCount =
          processingResultsData?.processingResults?.length || 0;

        // Generate upload URL for processing result
        const uploadResponse = await generateProcessingResultUploadUrl(jobId, {
          fileName: file.name,
          artifactType,
          description,
          contentType: file.type || "application/octet-stream",
          fileSize: file.size,
        });

        // Upload file to S3 using the pre-signed URL
        const uploadResult = await fetch(uploadResponse.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type || "application/octet-stream",
          },
        });

        if (!uploadResult.ok) {
          throw new Error(`S3 upload failed: ${uploadResult.statusText}`);
        }

        // S3 upload triggers an async Lambda that saves to DynamoDB
        // Poll for the new processing result to appear
        const maxAttempts = 10;
        const delayMs = 500;

        for (let attempts = 0; attempts < maxAttempts; attempts++) {
          // Wait before checking (start with immediate check on first attempt)
          if (attempts > 0) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));
          }

          // Refetch data to check for the new file
          const result = await refetchProcessingResults();
          const newCount = result.data?.processingResults?.length || 0;

          // Check if the new file appeared
          if (newCount > currentCount) {
            // Success! The UI will update automatically from the hook.
            break;
          }
        }

        // Always trigger a final refetch to ensure UI is updated
        refetchProcessingResults();
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : "Upload failed";
        throw new Error(`Failed to upload ${file.name}: ${errorMsg}`);
      }
    },
    [
      jobId,
      queryClient,
      refetchProcessingResults,
      processingResultsData,
      context,
      effectiveFirmId,
    ],
  );

  const handleUploadComplete = useCallback(
    async (files: FileUploadItem[]) => {
      const uploadedFileCount = files.length;
      const currentFileCount = job?.uploadedFiles?.length || 0;

      // Delay to show upload success message before closing dialog
      setTimeout(() => {
        setUploadDialogOpen(false);
      }, 3000);

      // Invalidate all job-related queries to update lists and current job across the app
      await invalidateJobRelatedQueries({
        firmId: effectiveFirmId || undefined, // Convert null to undefined
        jobId,
      });

      // Backend processes S3 uploads asynchronously via event triggers (2-5 seconds)
      // Start safe polling using our custom hook
      startPolling(currentFileCount + uploadedFileCount);
    },
    [
      notification,
      startPolling,
      invalidateJobRelatedQueries,
      effectiveFirmId,
      jobId,
      job,
    ],
  );

  const handleUploadError = useCallback(
    (error: Error) => {
      notification.showError(error.message);
    },
    [notification],
  );

  const handleSubmitSuccess = useCallback(
    async (job: JobResponse) => {
      notification.showSuccess('Job submitted successfully!');
      await refetch(); // Refresh job data to show updated status
      // Also invalidate all job lists so the left side updates
      await invalidateJobRelatedQueries({
        firmId: effectiveFirmId || undefined,
        jobId,
      });
    },
    [
      notification,
      refetch,
      invalidateJobRelatedQueries,
      effectiveFirmId,
      jobId,
    ],
  );

  const handleSubmitError = useCallback(
    (error: Error, job: JobResponse) => {
      notification.showError(
        `Failed to submit job ${job.jobId}: ${error.message}`,
      );
    },
    [notification],
  );

  // All ops-specific handlers now come from the useOpsJobActions hook

  let drawerWidth: number | string = 480;
  if (isMedium) {
    drawerWidth = isMobile ? "100vw" : "calc(100vw - 72px)";
  }

  // Create context value
  const contextValue = {
    job: job || null,
    isLoading,
    error,
    context,
    variant,
    uploadedFileItems,
    processingArtifactItems,
    isLoadingProcessingResults,
    processingResultsError,
    processingArtifactsRef:
      processingArtifactsRef as RefObject<ProcessingArtifactsRef>,
    opsActions,
    fileActions,
    onUploadedFileDelete: handleUploadedFileDelete,
    onProcessingArtifactDelete: handleProcessingArtifactDelete,
    onUploadClick: () => setUploadDialogOpen(true),
    onSubmitSuccess: handleSubmitSuccess,
    onSubmitError: handleSubmitError,
    onClose,
    onProcessingResultUpload:
      context === "ops" ? handleProcessingResultUpload : undefined,
    getProcessingResultFileUrl,
    onArtifactUpdate,
  };

  const drawerContent = (
    <JobDetailsProvider value={contextValue}>
      <Box
        sx={{
          width: variant === "panel" ? "100%" : drawerWidth,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        {job && <JobDetailsHeader />}

        {/* Loading State */}
        {isLoading && (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: drawerWidth,
            }}
          >
            <LoadingSpinner message="Loading job details..." />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">
              Failed to load job details. Please try again.
            </Alert>
          </Box>
        )}

        {/* Content */}
        {job &&
          !isLoading &&
          (context === "ops" ? (
            <OpsJobDetailsView />
          ) : (
            <ClientJobDetailsView />
          ))}

        {/* File Upload Dialog */}
        {job && (
          <FileUploadDialog
            mode="general"
            open={uploadDialogOpen}
            onClose={() => setUploadDialogOpen(false)}
            job={job}
            onUploadComplete={handleUploadComplete}
            onError={handleUploadError}
          />
        )}

        {/* File Viewer Dialog */}
        {job && (
          <FileViewerDialog
            open={fileViewerOpen}
            files={job.uploadedFiles || []}
            initialFileIndex={selectedFileIndex}
            onClose={() => setFileViewerOpen(false)}
            onDownload={(uploadedFile) => {
              // Convert UploadedFile to FileItem for our handler
              const fileItem: FileItem = {
                ...convertUploadedFileToGeneric(uploadedFile),
                metadata: { isProcessingResult: false },
              };
              return handleFileDownload(fileItem);
            }}
            getFileUrl={getCachedFilePreviewUrl}
            title="File Viewer"
          />
        )}

        {/* Job Assignment Dialog (Ops only) */}
        {context === "ops" && job && (
          <JobAssignmentDialog
            open={opsActions.assignmentDialogOpen}
            onClose={opsActions.handleAssignmentClose}
            job={job}
            onSuccess={opsActions.handleAssignmentSuccess}
            onError={opsActions.handleAssignmentError}
          />
        )}

        {/* Notification Component */}
        {notification.NotificationComponent}
      </Box>
    </JobDetailsProvider>
  );

  // If panel variant, return content directly (for use in split-pane layout)
  if (variant === "panel") {
    return drawerContent;
  }

  // Otherwise, return traditional drawer
  return (
    <Drawer
      anchor={anchor}
      open={open}
      onClose={onClose}
      variant="temporary"
      hideBackdrop
      ModalProps={{
        keepMounted: true, // Better mobile performance
      }}
      sx={{
        pointerEvents: "none",
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          maxWidth: "100vw",
          pointerEvents: "auto",
        },
      }}
      PaperProps={{
        elevation: 0,
        sx: {
          // 1. Set the 'top' position to the AppBar's height
          top: 65,
          borderLeft: isMedium ? 0 : 1,
          borderColor: "divider",
          maxHeight: "100vh",
          pb: "100px",
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}
